import { useTenantSettings } from '@/features/company/queries'
import { COUNTRY_LOCALE_MAP } from '@/lib/locale'

/**
 * Form defaults derived from tenant Regional Settings (Company page).
 * Use this for `useState` initializers in editors so a tenant in the UK
 * gets `GB` / `GBP` defaults instead of `US` / `USD`. Falls back to the
 * `US` / `USD` baseline only when the tenant query hasn't resolved yet.
 */
export function useRegionalDefaults() {
  const { data } = useTenantSettings()
  const t = data?.tenant
  const country = (t?.country || 'US').toUpperCase()
  const fallback = COUNTRY_LOCALE_MAP[country as keyof typeof COUNTRY_LOCALE_MAP]
    ?? COUNTRY_LOCALE_MAP.US
  const currency = (t?.currency || fallback.currency).toUpperCase()
  const fiscalStartMonth = typeof t?.fiscal_year_start === 'number'
    ? t.fiscal_year_start
    : fallback.fiscalStartMonth
  const locale = t?.locale || fallback.locale
  const timezone = t?.timezone || fallback.tzDefault
  return {
    /** ISO 3166-1 alpha-2 country code, uppercase, default 'US'. */
    country,
    /** ISO 4217 currency code, uppercase, default 'USD'. */
    currency,
    /** 1-12 fiscal year start month, default 1 (January). */
    fiscalStartMonth,
    /** BCP 47 locale, e.g. 'en-US', 'fr-FR'. */
    locale,
    /** IANA timezone, e.g. 'America/New_York'. */
    timezone,
    /** True once the tenant query has resolved at least once. */
    isLoaded: !!t,
  }
}
